// src/pages/document/DocumentDetailPage.tsx
// Notion-style document page with inline editing and block editor.

import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { EntityAvatar } from '@/components/EntityAvatar';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { Badge, Button, ErrorAlert } from '@react/components';
import { CreateTaskFromDocument } from '@/features/document/CreateTaskFromDocument';
import { DocumentTasksSection } from '@/features/document/DocumentTasksSection';
import { DocumentEditor } from '@/components/document/editor/DocumentEditor';
import { PagedEditor } from '@/components/document/PagedEditor';
import { importDocx, exportDocx } from '@/components/document/editor/docx-io';
import { saveAs } from 'file-saver';
import { BacklinksPanel } from '@/components/document/BacklinksPanel';
import { DocumentComplianceRail } from '@/features/document/DocumentComplianceRail';
import type { DocumentReviewAnchor } from '@/features/document/review';
import { loadAllDocumentBlocks } from '@/features/document/loadAllBlocks';
import { exportFormats } from '@/features/document/complianceLogic';
import { canEditDocument } from '@/features/document/canEdit';
import { entityStore } from '@/stores/EntityStore';
import { useTabId } from '@/components/tabs/TabContext';
import { entityToUrn } from '@/features/resources/logic';
import {
  documentStatusMap,
  documentTypeMap,
  documentConfidentialityMap,
  getStatusMapping,
  getDocumentStatus,
  getDocumentType,
  getDocumentOwner,
  getDocumentDepartment,
  getDocumentTags,
  formatDate,
  trackDocumentAccess,
  getFavoriteDocIds,
  toggleFavoriteDoc,
  formatDocumentNumber,
  getEffectiveDate,
  getNextReviewDate,
  getDocumentVersion,
  isControlledDocument,
} from '@/features/document/logic';
import type { DocumentBlock } from '@core/types/document';
import type { Backlink } from '@/features/document/types';

// ---------------------------------------------------------------------------
// Document Icon System (Lucide-style SVG icons)
// ---------------------------------------------------------------------------

const ICON_PATHS: Record<string, string> = {
  // Documents
  'file-text': 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M16 13H8M16 17H8M10 9H8',
  'file-check': 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M9 15l2 2 4-4',
  'file-lock': 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8ZM14 2v6h6M10 16h4v3h-4zM12 16v-2a2 2 0 1 0-4 0',
  'clipboard': 'M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2M9 2h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z',
  'notebook': 'M2 6h4M2 10h4M2 14h4M2 18h4M6 2v20M20 2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z',
  'book-open': 'M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2ZM22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7Z',
  'scroll': 'M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4M19 17V5a2 2 0 0 0-2-2H4',
  'book-marked': 'M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20M10 2v8l3-3 3 3V2',
  // Status & Actions
  'check-circle': 'M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3',
  'alert-circle': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 8v4M12 16h.01',
  'info': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 16v-4M12 8h.01',
  'shield': 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z',
  'lock': 'M19 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2ZM7 11V7a5 5 0 0 1 10 0v4',
  'flag': 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1ZM4 22v-7',
  'award': 'M12 15a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM8.21 13.89 7 23l5-3 5 3-1.21-9.12',
  'star': 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2Z',
  // Objects & Tools
  'settings': 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2ZM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  'lightbulb': 'M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2ZM9 18h6M10 22h4',
  'target': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12ZM12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  'key': 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78ZM15.54 8.46 19 12M17 6l4 4',
  'tag': 'M12 2H2v10l9.29 9.29a1 1 0 0 0 1.42 0l6.58-6.58a1 1 0 0 0 0-1.42ZM7 7h.01',
  'link': 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  'paperclip': 'M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48',
  'folder': 'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z',
  // Data & Charts
  'bar-chart': 'M12 20V10M18 20V4M6 20v-4',
  'trending-up': 'M22 7l-8.5 8.5-5-5L2 17',
  'pie-chart': 'M21.21 15.89A10 10 0 1 1 8 2.83M22 12A10 10 0 0 0 12 2v10Z',
  'database': 'M12 8a9 5 0 1 0 0-1 9 5 0 0 0 0 1ZM3 7v5c0 2.76 4.03 5 9 5s9-2.24 9-5V7M3 12v5c0 2.76 4.03 5 9 5s9-2.24 9-5v-5',
  'table': 'M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0H5a2 2 0 0 1-2-2v-4m6 6h10a2 2 0 0 0 2-2v-4M3 9h18M3 15h18',
  // Communication
  'mail': 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2ZM22 6l-10 7L2 6',
  'message-circle': 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z',
  'bell': 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0',
  // People & Users
  'users': 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  'user-check': 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM16 11l2 2 4-4',
  'briefcase': 'M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2ZM16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16',
  // Misc
  'zap': 'M13 2 3 14h9l-1 8 10-12h-9l1-8Z',
  'heart': 'M20.42 4.58a5.4 5.4 0 0 0-7.65 0L12 5.36l-.77-.78a5.4 5.4 0 0 0-7.65 0 5.4 5.4 0 0 0 0 7.65l.77.77L12 20.65l7.65-7.65.77-.77a5.4 5.4 0 0 0 0-7.65Z',
  'globe': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10Z',
  'compass': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM16.24 7.76l-2.12 6.36-6.36 2.12 2.12-6.36 6.36-2.12Z',
  'layers': 'M12 2 2 7l10 5 10-5-10-5ZM2 17l10 5 10-5M2 12l10 5 10-5',
  'hash': 'M4 9h16M4 15h16M10 3 8 21M16 3l-2 18',
  'calendar': 'M19 4H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2ZM16 2v4M8 2v4M3 10h18',
  'clock': 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  'search': 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
};

function DocumentIcon({ name, className = 'w-5 h-5' }: { name: string; className?: string }) {
  const d = ICON_PATHS[name];
  if (!d) return null;
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d={d} />
    </svg>
  );
}

const ICON_CATEGORIES = [
  { label: 'Documents', icons: ['file-text', 'file-check', 'file-lock', 'clipboard', 'notebook', 'book-open', 'scroll', 'book-marked'] },
  { label: 'Status', icons: ['check-circle', 'alert-circle', 'info', 'shield', 'lock', 'flag', 'award', 'star'] },
  { label: 'Tools', icons: ['settings', 'lightbulb', 'target', 'key', 'tag', 'link', 'paperclip', 'folder'] },
  { label: 'Data', icons: ['bar-chart', 'trending-up', 'pie-chart', 'database', 'table', 'search', 'hash', 'layers'] },
  { label: 'Other', icons: ['mail', 'message-circle', 'bell', 'users', 'user-check', 'briefcase', 'zap', 'heart', 'globe', 'compass', 'calendar', 'clock'] },
];

function IconPicker({ anchorRef, onSelect, onRemove, hasIcon, onClose }: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onSelect: (icon: string) => void;
  onRemove: () => void;
  hasIcon: boolean;
  onClose: () => void;
}) {
  return (
    <AnchoredMenu
      anchor={anchorRef}
      open
      onClose={onClose}
      role="dialog"
      width={300}
      className="p-3">
      {ICON_CATEGORIES.map(cat => (
        <div key={cat.label} className="mb-3">
          <div className="text-xs text-muted-foreground mb-1.5">{cat.label}</div>
          <div className="flex flex-wrap gap-0.5">
            {cat.icons.map(icon => (
              <button
                key={icon}
                onClick={() => { onSelect(icon); onClose(); }}
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={icon}
              >
                <DocumentIcon name={icon} className="w-[18px] h-[18px]" />
              </button>
            ))}
          </div>
        </div>
      ))}
      {hasIcon && (
        <button
          onClick={() => { onRemove(); onClose(); }}
          className="w-full mt-1 px-2 py-1.5 text-xs text-destructive hover:bg-destructive/10 rounded transition-colors text-center"
        >
          Remove icon
        </button>
      )}
    </AnchoredMenu>
  );
}

// ---------------------------------------------------------------------------
// Formal Document Header (for controlled documents)
// ---------------------------------------------------------------------------

function FormalDocumentHeader({ item, t, locale }: { item: any; t: (k: string, opts?: any) => string; locale: string }) {
  const status = getDocumentStatus(item);
  const docType = getDocumentType(item);
  const statusInfo = getStatusMapping(documentStatusMap, status);
  const typeInfo = getStatusMapping(documentTypeMap, docType);
  const docNumber = formatDocumentNumber(item);
  const version = getDocumentVersion(item);
  const effectiveDate = getEffectiveDate(item);
  const reviewDate = getNextReviewDate(item);
  const owner = getDocumentOwner(item);
  const conf = item?.confidentiality;
  const confInfo = conf ? getStatusMapping(documentConfidentialityMap, conf) : null;

  // Pipeline stages with active indicator
  const stages = [
    { key: 'DOCUMENT_STATUS_DRAFT', labelKey: 'document.pipeline.draft' },
    { key: 'DOCUMENT_STATUS_IN_REVIEW', labelKey: 'document.pipeline.inReview' },
    { key: 'DOCUMENT_STATUS_PENDING_APPROVAL', labelKey: 'document.pipeline.pendingApproval' },
    { key: 'DOCUMENT_STATUS_APPROVED', labelKey: 'document.pipeline.approved' },
    { key: 'DOCUMENT_STATUS_PUBLISHED', labelKey: 'document.pipeline.published' },
  ];
  const activeIdx = stages.findIndex((s) => s.key === status);

  return (
    <div className="mb-6 rounded-lg border border-border bg-card">
      {/* Top bar: number + type + confidentiality + status */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-border">
        {docNumber && (
          <span className="text-sm font-mono font-semibold text-foreground">{docNumber}</span>
        )}
        <Badge variant={typeInfo.variant as any} size="sm">{t(typeInfo.labelKey)}</Badge>
        {confInfo && <Badge variant={confInfo.variant as any} size="sm">{t(confInfo.labelKey)}</Badge>}
        {version && (
          <span className="text-xs text-muted-foreground">
            {t('document.fieldVersion')}: <span className="font-medium text-foreground">{version}</span>
          </span>
        )}

        <div className="flex-1" />

        <Badge variant={statusInfo.variant as any}>{t(statusInfo.labelKey)}</Badge>
      </div>

      {/* Pipeline progress bar */}
      <div className="px-5 py-3 border-b border-border" role="progressbar" aria-label={t('document.pipeline.ariaLabel')}>
        <div className="flex items-center gap-0">
          {stages.map((stage, i) => {
            const isPast = i < activeIdx;
            const isCurrent = i === activeIdx;
            return (
              <div key={stage.key} className="flex items-center flex-1 min-w-0">
                {/* Step dot */}
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border-2 ${
                  isPast ? 'bg-primary border-primary text-primary-foreground' :
                  isCurrent ? 'bg-background border-primary text-primary' :
                  'bg-background border-border text-muted-foreground'
                }`}>
                  {isPast ? (
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {/* Label */}
                <span className={`ml-1.5 text-xs truncate hidden sm:inline ${
                  isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'
                }`}>
                  {t(stage.labelKey)}
                </span>
                {/* Connector line */}
                {i < stages.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 rounded ${
                    isPast ? 'bg-primary' : 'bg-border'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Meta row: owner, dates */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-5 py-2.5 text-xs text-muted-foreground">
        {owner && (
          <span className="flex items-center gap-1.5">
            <EntityAvatar name={owner} image={item.owner?.avatar} userId={item.owner?.entity?.id} size="xs" className="h-4 w-4 text-[8px]" />
            {owner}
          </span>
        )}
        {effectiveDate && (
          <span>
            {t('document.fieldEffectiveDate')}: <span className="text-foreground">{formatDate(effectiveDate, locale)}</span>
          </span>
        )}
        {reviewDate && (
          <span>
            {t('document.fieldReviewDate')}: <span className="text-foreground">{formatDate(reviewDate, locale)}</span>
          </span>
        )}
        {item?.department && (
          <span>
            {t('document.fieldDepartment')}: <span className="text-foreground">{item.department}</span>
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cover URL Input
// ---------------------------------------------------------------------------

function CoverInput({ anchorRef, onSubmit, onClose }: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onSubmit: (url: string) => void;
  onClose: () => void;
}) {
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = () => {
    const trimmed = url.trim();
    if (trimmed) {
      onSubmit(trimmed);
      onClose();
    }
  };

  return (
    <AnchoredMenu
      anchor={anchorRef}
      open
      onClose={onClose}
      role="dialog"
      width={360}
      className="p-3">
      <div className="text-xs text-muted-foreground mb-2">Paste an image URL</div>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); if (e.key === 'Escape') onClose(); }}
          placeholder="https://example.com/image.jpg"
          className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <button
          onClick={handleSubmit}
          disabled={!url.trim()}
          className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          Add
        </button>
      </div>
    </AnchoredMenu>
  );
}

// ---------------------------------------------------------------------------
// Cover Component
// ---------------------------------------------------------------------------

function DocumentCover({ url, onRemove }: { url?: string; onRemove: () => void }) {
  if (!url) return null;
  return (
    <div className="relative group -mx-24 -mt-6 mb-6">
      <div className="h-[30vh] min-h-[200px] max-h-[320px] overflow-hidden">
        <img src={url} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
        <button
          onClick={onRemove}
          className="px-3 py-1.5 text-xs font-medium bg-background/80 backdrop-blur-sm border border-border rounded-md text-foreground hover:bg-background transition-colors"
        >
          Remove cover
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Breadcrumb
// ---------------------------------------------------------------------------

function Breadcrumb({ title }: { title: string }) {
  const { t } = useI18n();
  return (
    <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-6">
      <Link to="/documents" className="hover:text-foreground transition-colors">
        {t('document.list')}
      </Link>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
        <path d="M9 18l6-6-6-6" />
      </svg>
      <span className="text-foreground truncate max-w-[300px]">{title || t('common.untitled')}</span>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Properties Panel
// ---------------------------------------------------------------------------

interface PropertiesPanelProps {
  item: any;
  isOpen: boolean;
  onToggle: () => void;
}

function PropertiesPanel({ item, isOpen, onToggle }: PropertiesPanelProps) {
  const { t, locale } = useI18n();
  const status = getDocumentStatus(item);
  const docType = getDocumentType(item);
  const owner = getDocumentOwner(item);
  const department = getDocumentDepartment(item);
  const tags = getDocumentTags(item);

  const statusInfo = getStatusMapping(documentStatusMap, status);
  const typeInfo = getStatusMapping(documentTypeMap, docType);

  const docNumber = formatDocumentNumber(item);
  const version = getDocumentVersion(item);
  const effectiveDate = getEffectiveDate(item);
  const reviewDate = getNextReviewDate(item);

  const properties = [
    { label: t('document.fieldStatus'), value: status ? <Badge variant={statusInfo.variant as any} size="sm">{t(statusInfo.labelKey)}</Badge> : null },
    { label: t('document.fieldType'), value: docType ? <Badge variant={typeInfo.variant as any} size="sm">{t(typeInfo.labelKey)}</Badge> : null },
    { label: t('document.fieldNumber'), value: docNumber ? <span className="text-sm font-mono text-foreground">{docNumber}</span> : null },
    { label: t('document.fieldVersion'), value: version ? <Badge variant="secondary" size="sm">{version}</Badge> : null },
    {
      label: t('document.fieldOwner'),
      value: owner ? (
        <div className="flex items-center gap-2">
          <EntityAvatar name={owner} image={item.owner?.avatar} userId={item.owner?.entity?.id} size="xs" className="h-5 w-5 text-[9px]" />
          <span className="text-sm">{owner}</span>
        </div>
      ) : null,
    },
    { label: t('document.fieldDepartment'), value: department ? <span className="text-sm">{department}</span> : null },
    { label: t('document.fieldEffectiveDate'), value: effectiveDate ? <span className="text-sm text-muted-foreground">{formatDate(effectiveDate, locale)}</span> : null },
    { label: t('document.fieldReviewDate'), value: reviewDate ? <span className="text-sm text-muted-foreground">{formatDate(reviewDate, locale)}</span> : null },
    {
      label: t('document.fieldTags'),
      value: tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag: string) => (
            <span key={tag} className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {tag}
            </span>
          ))}
        </div>
      ) : null,
    },
    {
      label: t('document.fieldCreatedAt'),
      value: item?.created_at ? <span className="text-sm text-muted-foreground">{formatDate(item.created_at, locale)}</span> : null,
    },
    {
      label: t('document.fieldUpdatedAt'),
      value: item?.updated_at ? <span className="text-sm text-muted-foreground">{formatDate(item.updated_at, locale)}</span> : null,
    },
  ].filter(p => p.value !== null);

  return (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="currentColor"
          className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
        >
          <path d="M8 5l8 7-8 7z" />
        </svg>
        {locale.startsWith('ru') ? 'Свойства' : 'Properties'}
      </button>
      {isOpen && (
        <div className="border-t border-border pt-2 space-y-2">
          {properties.map((prop, i) => (
            <div key={i} className="flex items-start py-1.5 min-h-[32px]">
              <span className="w-[140px] flex-shrink-0 text-sm text-muted-foreground">{prop.label}</span>
              <div className="flex-1">{prop.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Action Bar
// ---------------------------------------------------------------------------

function ActionBar({
  documentId,
  onCreateTask,
  onDelete,
  onImportDocx,
  onExportDocx,
  onServerExport,
}: {
  documentId: string;
  onCreateTask: () => void;
  onDelete?: () => void;
  onImportDocx?: () => void;
  onExportDocx?: () => void;
  onServerExport?: (format: string) => void;
}) {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        size="sm"
        className="mr-1"
        onClick={onCreateTask}
        leftIcon={(
          <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
        )}
      >
        {t('document.tasks.create')}
      </Button>

      {/* Version history */}
      <Link
        to={`/documents/${documentId}/versions`}
        className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        title={t('document.versions')}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </Link>

      {/* Audit log */}
      <Link
        to={`/documents/${documentId}/audit-log`}
        className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        title={t('document.auditLog')}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      </Link>

      {/* More menu */}
      <div className="relative">
        <button
          ref={menuTriggerRef}
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="5" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="12" cy="19" r="1" />
          </svg>
        </button>
        <AnchoredMenu
          anchor={menuTriggerRef}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          align="end"
          ariaLabel={t('common.actions')}
          className="w-48 py-1"
        >
          <Link
            to={`/documents/${documentId}/acknowledgments`}
            className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
            onClick={() => setMenuOpen(false)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
            </svg>
            {t('document.acknowledgments')}
          </Link>
          {onImportDocx && (
            <button
              onClick={() => { setMenuOpen(false); onImportDocx(); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              {t('document.importDocx')}
            </button>
          )}
          {onExportDocx && (
            <button
              onClick={() => { setMenuOpen(false); onExportDocx(); }}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {t('document.exportDocx')}
            </button>
          )}
          {onServerExport && (
            <>
              <div className="h-px bg-border mx-2 my-1" />
              <div className="px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('document.export.title')}
              </div>
              {exportFormats.map((f) => (
                <button
                  key={f.value}
                  onClick={() => { setMenuOpen(false); onServerExport(f.value); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-foreground hover:bg-accent transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {t(f.labelKey)}
                </button>
              ))}
            </>
          )}
          {onDelete && (
            <>
              <div className="h-px bg-border mx-2 my-1" />
              <button
                onClick={() => { setMenuOpen(false); onDelete(); }}
                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-accent transition-colors"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                {t('common.delete')}
              </button>
            </>
          )}
        </AnchoredMenu>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export const DocumentDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useDocument();
  const scope = useScope();
  const { t, locale } = useI18n();
  const tabId = useTabId();

  const item = store.currentItem;
  const [blocks, setBlocks] = useState<DocumentBlock[]>([]);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [contentError, setContentError] = useState(false);
  const [contentReloadKey, setContentReloadKey] = useState(0);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [backlinks] = useState<Backlink[]>([]);
  const [isLoadingBacklinks] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(() => getFavoriteDocIds());

  const [showIconPicker, setShowIconPicker] = useState(false);
  const iconTriggerRef = useRef<HTMLButtonElement>(null);
  const addIconTriggerRef = useRef<HTMLButtonElement>(null);
  const [showCoverInput, setShowCoverInput] = useState(false);
  const coverTriggerRef = useRef<HTMLButtonElement>(null);
  const [railOpen, setRailOpen] = useState(false);
  const [reviewAnchor, setReviewAnchor] = useState<DocumentReviewAnchor | null>(null);
  const [lock, setLock] = useState<any>(null);
  const [lockBusy, setLockBusy] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskRefreshKey, setTaskRefreshKey] = useState(0);

  const handleReviewBlock = useCallback((anchor: DocumentReviewAnchor) => {
    setReviewAnchor(anchor);
    setRailOpen(true);
  }, []);

  // Editor instance ref for DOCX import/export
  const editorRef = useRef<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [transferError, setTransferError] = useState<string | null>(null);

  const titleRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const isFavorite = id ? favoriteIds.has(id) : false;

  const handleToggleFavorite = useCallback(() => {
    if (!id) return;
    setFavoriteIds(toggleFavoriteDoc(id));
  }, [id]);

  // DOCX Import
  const handleImportDocx = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!editorRef.current) {
      setTransferError(t('document.transfer.editorUnavailable'));
      return;
    }
    setTransferError(null);
    const result = await importDocx(file, editorRef.current);
    if (!result.ok) {
      setTransferError(`${t('document.transfer.importFailed')}: ${result.error || t('common.unknown')}`);
    }
    if (result.warnings.length > 0) {
      console.warn('[DOCX Import] Warnings:', result.warnings);
    }
    // Reset input so same file can be re-imported
  }, [t]);

  // DOCX Export
  const handleExportDocx = useCallback(async () => {
    if (!editorRef.current) {
      setTransferError(t('document.transfer.editorUnavailable'));
      return;
    }
    setTransferError(null);
    const ps = (item as any)?.page_settings;
    const dims = ps ? {
      pageWidth: ps.paper_size === 'PAPER_SIZE_LETTER' ? 216 : ps.paper_size === 'PAPER_SIZE_LEGAL' ? 216 : ps.paper_size === 'PAPER_SIZE_A3' ? 297 : 210,
      pageHeight: ps.paper_size === 'PAPER_SIZE_LETTER' ? 279 : ps.paper_size === 'PAPER_SIZE_LEGAL' ? 356 : ps.paper_size === 'PAPER_SIZE_A3' ? 420 : 297,
      marginTop: ps.margins?.top_mm ?? 20,
      marginBottom: ps.margins?.bottom_mm ?? 20,
      marginLeft: ps.margins?.left_mm ?? 30,
      marginRight: ps.margins?.right_mm ?? 15,
    } : {};
    try {
      await exportDocx(editorRef.current, {
        title: title || 'Document',
        ...dims,
        pageNumbers: true,
      });
    } catch (err) {
      setTransferError(`${t('document.transfer.exportFailed')}: ${err instanceof Error ? err.message : t('common.unknown')}`);
    }
  }, [item, title, t]);

  // Load document
  useEffect(() => {
    if (id) {
      store.GetDocument(id);
    }
    return () => {
      store.clearCurrent();
      entityStore.clearContext(tabId);
    };
  }, [id]);

  // Publish entity context for the right rail (discussion/tasks/schedule tabs).
  useEffect(() => {
    const entity = (item as any)?.entity;
    if (entity?.id) {
      entityStore.loadEntityData(tabId, {
        urn: entity.urn || entityToUrn(entity),
        type: entity.type_name || 'document',
        id: entity.id,
        title: (item as any)?.title || '',
      });
    }
  }, [(item as any)?.entity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track document access for recent docs
  useEffect(() => {
    if (item) {
      trackDocumentAccess(item);
    }
  }, [item]);

  // Load blocks
  useEffect(() => {
    if (!id) return;
    let active = true;
    setIsLoadingContent(true);
    setContentError(false);
    loadAllDocumentBlocks(id)
      .then((content) => { if (active) setBlocks(content); })
      .catch(() => { if (active) setContentError(true); })
      .finally(() => { if (active) setIsLoadingContent(false); });
    return () => { active = false; };
  }, [id, contentReloadKey]);

  // Sync title from loaded item
  useEffect(() => {
    if (item) {
      setTitle((item as any).title || '');
      setDescription((item as any).description || '');
    }
  }, [item]);

  // Debounced title save
  const saveTitle = useCallback(
    (newTitle: string) => {
      if (!id || !item) return;
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(async () => {
        setIsSavingTitle(true);
        try {
          await store.UpdateDocument(id, {
            document: { ...item, title: newTitle },
            field_mask: ['title'],
          });
        } catch {
          // handled by store
        } finally {
          setIsSavingTitle(false);
        }
      }, 800);
    },
    [id, item, scope.productId, store],
  );

  const handleTitleInput = useCallback(() => {
    const text = titleRef.current?.textContent ?? '';
    setTitle(text);
    saveTitle(text);
  }, [saveTitle]);

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Focus first block
      const firstBlock = document.querySelector('[data-block-editor] [contenteditable]') as HTMLElement;
      firstBlock?.focus();
    }
  };

  // Pessimistic editing lock
  const handleToggleLock = useCallback(async () => {
    if (!id) return;
    setLockBusy(true);
    try {
      if (lock) {
        await store.ReleaseDocumentLock(id);
        setLock(null);
      } else {
        const res = await store.AcquireDocumentLock(id, { document_id: id } as any);
        setLock((res as any)?.lock ?? null);
      }
    } catch {
      // handled by store
    } finally {
      setLockBusy(false);
    }
  }, [id, lock, store]);

  // Server-side export (PDF / HTML / Markdown)
  const handleServerExport = useCallback(
    async (format: string) => {
      if (!id) return;
      setTransferError(null);
      try {
        const res = await store.ExportDocument(id, { document_id: id, format } as any);
        const url = (res as any)?.download_url;
        if (!url) throw new Error(t('document.transfer.noDownload'));
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status}`);
        const extension = format === 'EXPORT_FORMAT_HTML' ? 'html' : 'md';
        saveAs(await response.blob(), `${(title || 'document').replace(/[^a-zA-Zа-яА-Я0-9_-]/g, '_')}.${extension}`);
      } catch (err) {
        setTransferError(`${t('document.transfer.exportFailed')}: ${err instanceof Error ? err.message : t('common.unknown')}`);
      }
    },
    [id, store, t, title],
  );

  const handleDelete = async () => {
    if (!id || !confirm(t('document.deleteConfirm'))) return;
    try {
      await store.DeleteDocument(id);
      navigate('/documents');
    } catch {
      // Error handled by store
    }
  };

  const handleSetIcon = useCallback(
    (icon: string) => {
      if (!id || !item) return;
      store.UpdateDocument(id, {
        document: { ...item, icon },
        field_mask: ['icon'],
      }).catch(() => {});
    },
    [id, item, scope.productId, store],
  );

  const handleRemoveIcon = useCallback(() => {
    if (!id || !item) return;
    store.UpdateDocument(id, {
      document: { ...item, icon: '' },
      field_mask: ['icon'],
    }).catch(() => {});
  }, [id, item, scope.productId, store]);

  const handleSetCover = useCallback(
    (cover_url: string) => {
      if (!id || !item) return;
      store.UpdateDocument(id, {
        document: { ...item, cover_url },
        field_mask: ['cover_url'],
      }).catch(() => {});
    },
    [id, item, scope.productId, store],
  );

  // Block callbacks
  const handleBlockUpdate = useCallback(
    (block: DocumentBlock) => {
      if (!id || !block.entity?.id) return Promise.reject(new Error('Block has no ID'));
      return store.UpdateBlock(id, block.entity.id, {
          block: {
            type: block.type,
            rich_text: block.rich_text,
            to_do: block.to_do,
            callout: block.callout,
            code: block.code,
            heading: block.heading,
          },
        } as any);
    },
    [id, store],
  );

  const handleBlockCreate = useCallback(
    (block: DocumentBlock, afterBlockId?: string) => {
      if (!id) return Promise.reject(new Error('Document has no ID'));
      return store.AppendBlocks(id, {
        blocks: [block],
        after_block_id: afterBlockId,
      } as any);
    },
    [id, store],
  );

  const handleBlocksCreate = useCallback(
    (newBlocks: DocumentBlock[], afterBlockId?: string) => {
      if (!id) return Promise.reject(new Error('Document has no ID'));
      return store.AppendBlocks(id, {
        blocks: newBlocks,
        after_block_id: afterBlockId,
      });
    },
    [id, store],
  );

  const handleBlocksModify = useCallback(
    (updates: DocumentBlock[], deletes: string[]) => {
      if (!id) return Promise.reject(new Error('Document has no ID'));
      return store.BatchUpdateBlocks(id, {
        operations: [
          ...updates.map((block) => ({ update: { block } })),
          ...deletes.map((block_id) => ({ delete: { block_id } })),
        ],
      });
    },
    [id, store],
  );

  const handleBlockDelete = useCallback(
    (blockId: string) => {
      if (!id) return Promise.reject(new Error('Document has no ID'));
      return store.DeleteBlock(id, blockId);
    },
    [id, store],
  );

  // Loading
  if (store.isLoading && !item) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-12">
        {/* Skeleton */}
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-32 rounded bg-muted" />
          <div className="h-12 w-3/4 rounded bg-muted mt-8" />
          <div className="h-4 w-1/2 rounded bg-muted" />
          <div className="space-y-2 mt-8">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-4 rounded bg-muted" style={{ width: `${60 + Math.random() * 30}%` }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (store.error && !item) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-12">
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
        <Link to="/documents" className="mt-4 inline-block text-sm text-primary hover:underline">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-12 text-center">
        <p className="text-muted-foreground">{t('common.notFound')}</p>
        <Link to="/documents" className="mt-4 inline-block text-sm text-primary hover:underline">
          {t('common.backToList')}
        </Link>
      </div>
    );
  }

  const isControlled = isControlledDocument(item);
  const canEdit = !isControlled || canEditDocument(item);

  return (
    <div className="flex flex-col lg:flex-row">
      <div className={`flex-1 min-w-0 mx-auto ${isControlled ? 'max-w-[1000px] px-4 py-4' : 'max-w-[820px] px-6 sm:px-10 lg:px-16 pt-5 pb-24'}`}>
      {transferError && <ErrorAlert error={transferError} dismissible onDismiss={() => setTransferError(null)} />}
      {/* Cover (wiki mode only) */}
      {!isControlled && (
        <DocumentCover
          url={(item as any).cover_url}
          onRemove={() => {
            if (!id) return;
            store.UpdateDocument(id, {
              document: { ...item, cover_url: '' },
              field_mask: ['cover_url'],
            }).catch(() => {});
          }}
        />
      )}

      {/* Breadcrumb + Actions */}
      <div className="flex items-center justify-between">
        {isControlled ? <Breadcrumb title={title} /> : <div />}
        <div className="flex items-center gap-1">
          {/* Favorite toggle */}
          <button
            onClick={handleToggleFavorite}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label={isFavorite ? t('document.removeFavorite') : t('document.addFavorite')}
          >
            <svg
              className={`w-4 h-4 ${isFavorite ? 'text-yellow-500 fill-yellow-500' : ''}`}
              viewBox="0 0 24 24"
              fill={isFavorite ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth={2}
            >
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
          {/* Editing lock */}
          {canEdit && (
            <button
              onClick={handleToggleLock}
              disabled={lockBusy}
              className={`p-2 rounded-md transition-colors ${lock ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'} disabled:opacity-40`}
              aria-label={lock ? t('document.lock.release') : t('document.lock.acquire')}
              title={lock ? t('document.lock.lockedBy', { name: lock?.locked_by?.display_name || t('document.lock.you') }) : t('document.lock.acquire')}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                {lock ? (
                  <>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </>
                ) : (
                  <>
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                  </>
                )}
              </svg>
            </button>
          )}
          {/* Compliance rail toggle */}
          <button
            onClick={() => setRailOpen((v) => !v)}
            className={`p-2 rounded-md transition-colors ${railOpen ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
            aria-label={t('document.rail.toggle')}
            aria-pressed={railOpen}
            title={t('document.rail.title')}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M15 3v18" />
            </svg>
          </button>
          <ActionBar
            documentId={id!}
            onCreateTask={() => setTaskDialogOpen(true)}
            onDelete={canEdit ? handleDelete : undefined}
            onImportDocx={canEdit ? handleImportDocx : undefined}
            onExportDocx={handleExportDocx}
            onServerExport={handleServerExport}
          />
        </div>
      </div>

      {/* Formal header (controlled documents) */}
      {isControlled && <FormalDocumentHeader item={item} t={t} locale={locale} />}

      {/* Icon (wiki mode only) */}
      {!isControlled && (item as any).icon && ICON_PATHS[(item as any).icon] && (
        <div className="relative inline-block">
          <button
            ref={iconTriggerRef}
            onClick={() => setShowIconPicker(!showIconPicker)}
            className="mb-2 p-2 rounded-lg cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <DocumentIcon name={(item as any).icon} className="w-10 h-10" />
          </button>
          {showIconPicker && (
            <IconPicker
              anchorRef={iconTriggerRef}
              onSelect={handleSetIcon}
              onRemove={handleRemoveIcon}
              hasIcon
              onClose={() => setShowIconPicker(false)}
            />
          )}
        </div>
      )}

      {/* Hover actions: Add icon, Add cover (wiki mode only) */}
      {!isControlled && !((item as any).icon && (item as any).cover_url) && (
        <div className="flex items-center gap-1.5 mb-3 opacity-0 hover:opacity-100 focus-within:opacity-100 transition-opacity">
          {!(item as any).icon && (
            <div className="relative">
              <button
                ref={addIconTriggerRef}
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground bg-muted/60 hover:bg-muted hover:text-foreground border border-transparent hover:border-border rounded-md transition-all"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="9" r="2" />
                  <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" />
                  <path d="M18 14v4M16 16h4" />
                </svg>
                {locale.startsWith('ru') ? 'Добавить значок' : 'Add icon'}
              </button>
              {showIconPicker && (
                <IconPicker
                  anchorRef={addIconTriggerRef}
                  onSelect={handleSetIcon}
                  onRemove={handleRemoveIcon}
                  hasIcon={false}
                  onClose={() => setShowIconPicker(false)}
                />
              )}
            </div>
          )}
          {!(item as any).cover_url && (
            <div className="relative">
              <button
                ref={coverTriggerRef}
                onClick={() => setShowCoverInput(!showCoverInput)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-muted-foreground bg-muted/60 hover:bg-muted hover:text-foreground border border-transparent hover:border-border rounded-md transition-all"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <path d="m21 15-5-5L5 21" />
                </svg>
                {locale.startsWith('ru') ? 'Добавить обложку' : 'Add cover'}
              </button>
              {showCoverInput && (
                <CoverInput
                  anchorRef={coverTriggerRef}
                  onSubmit={handleSetCover}
                  onClose={() => setShowCoverInput(false)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Title */}
      <div
        ref={titleRef}
        contentEditable={canEdit}
        suppressContentEditableWarning
        role={canEdit ? 'textbox' : 'heading'}
        aria-level={canEdit ? undefined : 1}
        aria-multiline={canEdit ? false : undefined}
        aria-readonly={!canEdit}
        aria-label={t('document.fieldTitle')}
        tabIndex={canEdit ? 0 : undefined}
        onInput={canEdit ? handleTitleInput : undefined}
        onKeyDown={canEdit ? handleTitleKeyDown : undefined}
        data-placeholder={t('common.untitled')}
        className="text-[2rem] sm:text-[2.5rem] leading-[1.2] tracking-[-0.025em] font-bold text-foreground outline-none mt-4 mb-2 empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/30"
      >
        {title}
      </div>

      {/* Description (subtle) */}
      {description && (
        <p className="text-base leading-relaxed text-muted-foreground mb-7">{description}</p>
      )}

      {/* Save indicator */}
      {isSavingTitle && (
        <div className="text-xs text-muted-foreground mb-2">Saving...</div>
      )}

      {/* Properties (wiki mode — controlled uses FormalDocumentHeader instead) */}
      {!isControlled && (
        <PropertiesPanel
          item={item}
          isOpen={propertiesOpen}
          onToggle={() => setPropertiesOpen(!propertiesOpen)}
        />
      )}

      {/* Editor */}
      {isLoadingContent ? (
        <div className="animate-pulse space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-5 rounded bg-muted" style={{ width: `${50 + Math.random() * 40}%` }} />
          ))}
        </div>
      ) : contentError ? (
        <div role="alert" className="space-y-3 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          <p>Не удалось загрузить содержимое полностью. Редактирование недоступно, чтобы не потерять блоки.</p>
          <button type="button" className="font-medium underline" onClick={() => setContentReloadKey((key) => key + 1)}>Повторить загрузку</button>
        </div>
      ) : isControlled ? (
        /* Paged editor — Word-style with A4 pages, margins, headers/footers */
        <PagedEditor
          documentId={id!}
          document={item}
          initialBlocks={blocks}
          onBlockUpdate={handleBlockUpdate}
          onBlockCreate={handleBlockCreate}
          onBlocksCreate={handleBlocksCreate}
          onBlocksModify={handleBlocksModify}
          onBlockDelete={handleBlockDelete}
          onEditorReady={(editor) => { editorRef.current = editor; }}
          onReviewBlock={handleReviewBlock}
          readOnly={!canEdit}
          onPageSettingsChange={(patch) => {
            if (!id || !canEdit) return;
            const current = (item as any).page_settings || {};
            store.UpdateDocument(id, {
              document: { ...item, page_settings: { ...current, ...patch } },
              field_mask: ['page_settings'],
            }).catch((err) => {
              console.error('[DocumentDetailPage] Failed to update page settings:', err);
            });
          }}
        />
      ) : (
        /* Notion-style infinite canvas editor */
        <div data-block-editor>
          <DocumentEditor
            documentId={id!}
            initialBlocks={blocks}
            onBlockUpdate={handleBlockUpdate}
            onBlockCreate={handleBlockCreate}
            onBlocksCreate={handleBlocksCreate}
            onBlocksModify={handleBlocksModify}
            onBlockDelete={handleBlockDelete}
            onEditorReady={(editor) => { editorRef.current = editor; }}
            onReviewBlock={handleReviewBlock}
          />
        </div>
      )}

      <DocumentTasksSection documentId={id!} refreshKey={taskRefreshKey} />

      {/* Backlinks */}
      <BacklinksPanel
        backlinks={backlinks}
        isLoading={isLoadingBacklinks}
        onNavigate={(docId) => navigate(`/documents/${docId}`)}
      />

      {/* Hidden file input for DOCX import */}
      {canEdit && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={handleFileSelected}
        />
      )}
      </div>

      {/* Compliance right rail */}
      {railOpen && id && (
        <DocumentComplianceRail
          documentId={id}
          productId={scope.productId}
          item={item}
          reviewAnchor={reviewAnchor}
          onClose={() => setRailOpen(false)}
          onChanged={() => { store.GetDocument(id); }}
          onSuggestionAccepted={() => setContentReloadKey((key) => key + 1)}
        />
      )}

      <CreateTaskFromDocument
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        item={item}
        onCreated={() => setTaskRefreshKey((key) => key + 1)}
      />
    </div>
  );
});

export default DocumentDetailPage;
